// Kit 03 · items 54, 55, 56, 57, 64. The layer system.
//
//   54/55 Drawer   width 440 · sticky header pad 20 · scrim over canvas · radius 0, full height
//   56    Confirm  width 440 · pad 24 · radius 16 · button row gap 12
//   57    Form     width 560 · pad 24 · radius 16 · field gap 16
//   64    Scrim    one opacity, one colour · light 45 percent · dark 65 percent
//
// ── The one rule this file exists to make structural ────────────────────────
//
// "One scrim at a time. A modal opened from a drawer reuses the existing scrim
// rather than stacking a second." (item 64 data-coexist, and the same sentence
// again on 54 and 56.)
//
// A component that renders its own scrim cannot obey that, because it cannot
// see the other one. So no component here renders a scrim. `OverlayProvider`
// keeps the stack of open layers and renders exactly ONE scrim element, whose
// tier is DERIVED from what is in the stack:
//
//   nothing open        no scrim
//   a drawer            --z-scrim-page 190, below --z-drawer 200
//   a drawer + a modal  --z-scrim-overlay 300, above the drawer
//
// That is the whole of item 64's keyboard note expressed as one computed
// value. It cannot disagree with itself, and a second drawer opening does not
// darken the page twice, because the scrim is one element regardless of the
// depth of the stack.
//
// ── Inert ──────────────────────────────────────────────────────────────────
//
// Item 54: "the page behind is marked inert, so it is neither tabbable nor
// announced". `inert` is a DOM property here rather than a JSX attribute
// because React 18 does not type it, and setting it through a ref works on
// both 18 and 19. It goes on the page wrapper, not on document.body, so the
// overlay host itself is never inside the inert subtree.

import {
  createContext, useCallback, useContext, useEffect, useId, useLayoutEffect,
  useMemo, useRef, useState,
} from "react";
import { createPortal } from "react-dom";
import type { ReactNode, RefObject } from "react";
import { X } from "lucide-react";
import { IconButton } from "./button";
import { cx } from "./kit-state";
import type { KitState, Stateful } from "./kit-state";

/* ── the stack ──────────────────────────────────────────────────────────── */

/** The two layer kinds that dim the page. A popover, tooltip, menu or the
    assistant panel are NOT here: none of them takes the page away. */
export type LayerKind = "drawer" | "modal";

type Layer = { id: string; kind: LayerKind; onDismiss?: () => void };

interface OverlayApi {
  /** Register an open layer. Returns the unregister. */
  open: (layer: Layer) => () => void;
  /** Where a panel renders. Never inside the page wrapper — see below. */
  host: HTMLElement | null;
}

const OverlayContext = createContext<OverlayApi | null>(null);

/**
 * Wrap the app root once. Every drawer and modal inside it shares one scrim
 * and one inert page.
 *
 * ── Why the panels are portalled ────────────────────────────────────────────
 *
 * `inert` applies to a whole subtree. A drawer written as a child of the
 * screen that opened it would therefore be inside the wrapper this provider
 * marks inert, and the drawer would go untabbable at exactly the moment it
 * opened — a focus trap around nothing, with the close button unreachable.
 *
 * The trap for this is that it LOOKS right: the panel paints, the scrim
 * paints, the z ladder is correct, and the only symptom is that Tab does not
 * move. So the panels render into the host, which is a sibling of the inert
 * wrapper and never inside it.
 *
 * `contained` puts the host on `position: absolute` rather than `fixed`, which
 * is what lets the gallery show an overlay inside a card-sized frame instead of
 * across the whole screenshot. It changes no z value and no behaviour.
 */
export function OverlayProvider({
  children, contained = false, className,
}: {
  children: ReactNode; contained?: boolean; className?: string;
}) {
  const [stack, setStack] = useState<Layer[]>([]);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const page = useRef<HTMLDivElement | null>(null);
  // The authoritative stack, mutated synchronously. `stack` is the same list
  // one render later and is what the scrim paints from; this is what the
  // Escape listener and the inert flag read, because both have to be right
  // DURING an event rather than after the next commit.
  const live = useRef<Layer[]>([]);

  const syncInert = useCallback(() => {
    if (page.current) page.current.inert = live.current.length > 0;
  }, []);

  const open = useCallback((layer: Layer) => {
    live.current = [...live.current, layer];
    syncInert();
    setStack(live.current);
    return () => {
      live.current = live.current.filter((l) => l.id !== layer.id);
      // Inert is cleared HERE, synchronously, inside the closing layer's own
      // cleanup — and this cleanup is declared first in Drawer and Modal, so it
      // runs before useReturnFocus's.
      //
      // The gate caught the version that did not. With inert cleared by an
      // effect keyed on the stack length, React ran the drawer's cleanups
      // first: focus() was called on a trigger that was still inside an inert
      // subtree, which is a silent no-op, and focus landed on <body>. Every
      // screenshot of it was correct. "Focus returns to the trigger" is half of
      // Phase 2's stated gate and it was failing in a way nothing but a driven
      // browser could see.
      syncInert();
      setStack(live.current);
    };
  }, [syncInert]);

  const api = useMemo<OverlayApi>(() => ({ open, host }), [open, host]);

  const top = stack[stack.length - 1];
  // Derived, not stored. A modal anywhere in the stack raises the one scrim
  // above the drawer; a drawer alone leaves it below the panel it belongs to.
  const tier = stack.some((l) => l.kind === "modal") ? "overlay" : "page";

  /**
   * One Escape listener for the whole stack, dispatched to the top layer only.
   *
   * The first version gave every layer its own document listener and had each
   * one call stopPropagation. That does not do what it looks like it does:
   * listeners on the SAME node all run regardless, so stopPropagation between
   * them is inert, and which one "wins" comes down to the order React happened
   * to attach them in — which changes whenever a parent re-renders, because an
   * effect that re-subscribes moves its listener to the end of the list.
   *
   * The gate caught it: Escape on a modal opened from a drawer closed the
   * DRAWER and left the modal standing. One listener reading the stack is the
   * only version where "one Escape closes one thing" is a property of the
   * system rather than of an ordering nobody controls.
   */
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      const layer = live.current[live.current.length - 1];
      if (!layer) return;
      event.stopPropagation();
      layer.onDismiss?.();
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, []);

  return (
    <OverlayContext.Provider value={api}>
      <div ref={page} className={cx("k-overlay-page", className)}>
        {children}
      </div>
      <div ref={setHost} className="k-overlay-host" data-contained={contained || undefined}>
        {stack.length > 0 ? <Scrim tier={tier} onDismiss={top?.onDismiss} /> : null}
      </div>
    </OverlayContext.Provider>
  );
}

/**
 * 64 · the scrim. One opacity, one colour, one element.
 *
 * Exported so the gallery can show it on its own, but nothing in the product
 * renders it directly: `OverlayProvider` owns the only instance, which is what
 * makes "one scrim at a time" true by construction rather than by convention.
 */
export function Scrim({
  tier = "page", onDismiss, state,
}: Stateful & { tier?: "page" | "overlay"; onDismiss?: () => void }) {
  return (
    <div
      className="k-scrim"
      data-tier={tier}
      data-state={state}
      aria-hidden="true"
      onClick={onDismiss}
    />
  );
}

/* ── the shared behaviour ───────────────────────────────────────────────── */

const FOCUSABLE = [
  "a[href]", "button:not([disabled])", "input:not([disabled])",
  "select:not([disabled])", "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function focusables(root: HTMLElement | null): HTMLElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE))
    .filter((el) => el.offsetParent !== null || el === document.activeElement);
}

/**
 * Tab cycles inside the panel, Escape closes, and focus goes back to whatever
 * opened it. Three behaviours rather than one because a caller can want the
 * last two without the first — the assistant (item 65) is explicitly "focus is
 * NOT trapped: the assistant reads the page behind it".
 */
export function useFocusTrap(ref: RefObject<HTMLElement | null>, active: boolean) {
  useEffect(() => {
    if (!active) return;
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Tab") return;
      const items = focusables(ref.current);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const here = document.activeElement;
      if (event.shiftKey && (here === first || !ref.current?.contains(here))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && here === last) {
        event.preventDefault();
        first.focus();
      }
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [ref, active]);
}

/** Escape closes. Registered on the document, so it fires whether focus is on
    the panel or on something inside it. */
export function useEscape(active: boolean, onEscape?: () => void) {
  useEffect(() => {
    if (!active || !onEscape) return;
    function onKey(event: KeyboardEvent) {
      if (event.key !== "Escape") return;
      // The topmost layer only. A modal over a drawer stops the key here, so
      // one Escape closes one thing.
      event.stopPropagation();
      onEscape?.();
    }
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [active, onEscape]);
}

/**
 * Focus opens somewhere inside and returns to the trigger on close.
 *
 * `initial` is which control takes it. Item 56 is explicit that a destructive
 * confirm opens on CANCEL, not on the destructive action, so this is a prop
 * and not "the first focusable".
 */
export function useReturnFocus(
  active: boolean,
  panel: RefObject<HTMLElement | null>,
  initial?: RefObject<HTMLElement | null>,
) {
  const trigger = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!active) return;
    trigger.current = document.activeElement as HTMLElement | null;
    const target = initial?.current ?? focusables(panel.current)[0] ?? panel.current;
    target?.focus();
    return () => {
      // Only if the trigger is still on the page. A drawer that removed its own
      // trigger would otherwise send focus to a detached node, and the browser
      // silently drops it on body.
      const back = trigger.current;
      if (back && document.contains(back)) back.focus();
    };
  }, [active, panel, initial]);
}

/**
 * Register with the provider for as long as this layer is open.
 *
 * useLayoutEffect, not useEffect, and the difference is visible.
 *
 * The scrim's tier is computed from the stack, so registering in a passive
 * effect meant the modal's own commit painted first and the scrim raised to
 * 300 on the NEXT one. For one frame a modal sat above a scrim still at 190,
 * with the drawer it was opened from undimmed underneath it — the exact thing
 * item 64's coexistence rule exists to prevent, for one sixtieth of a second.
 *
 * The gate saw it as flakiness: the same assertion passed and failed on
 * alternate runs depending on where Playwright's poll landed between the two
 * commits. A layout effect closes the window, because React flushes the
 * resulting re-render before the browser paints.
 */
function useLayer(kind: LayerKind, open: boolean, onDismiss?: () => void) {
  const api = useContext(OverlayContext);
  const id = useId();
  const dismiss = useRef(onDismiss);
  dismiss.current = onDismiss;
  useLayoutEffect(() => {
    if (!open || !api) return;
    return api.open({ id, kind, onDismiss: () => dismiss.current?.() });
  }, [api, id, kind, open]);
  return api;
}

/**
 * Render into the overlay host, or in place if there is no provider.
 *
 * The fallback is not a convenience. A panel that threw without a provider
 * would make `OverlayProvider` a thing every test, story and screenshot has to
 * remember; a panel that renders in place instead simply has no scrim and no
 * inert page, which is visible immediately and is the honest failure.
 */
function useHostPortal(node: ReactNode, active: boolean) {
  const api = useContext(OverlayContext);
  if (!active) return null;
  return api?.host ? createPortal(node, api.host) : node;
}

/* ═══ 54, 55 · the drawer ═══════════════════════════════════════════════════
   redline: width 440 · sticky header pad 20 · scrim over canvas · radius 0,
   full height
   keys: Escape closes and returns focus to the trigger. Focus is trapped while
   open and the page behind is marked inert. Tab cycles inside the panel.
   ═════════════════════════════════════════════════════════════════════════ */

export interface DrawerProps extends Stateful {
  open: boolean;
  onClose: () => void;
  /** The uppercase line above the title. Item 54 draws "Evidence". */
  eyebrow?: string;
  title: string;
  /** The provenance line under the title. Never invented: a drawer with no
      measurement to cite passes nothing and the line does not render. */
  meta?: ReactNode;
  children: ReactNode;
  /** Where focus opens. Defaults to the first focusable, which is the close. */
  initialFocus?: RefObject<HTMLElement | null>;
  closeLabel?: string;
  className?: string;
}

export function Drawer({
  open, onClose, eyebrow, title, meta, children, initialFocus, closeLabel = "Close", state, className,
}: DrawerProps) {
  const panel = useRef<HTMLDivElement | null>(null);
  const live = open || state === "open";

  // The provider owns Escape for anything on the stack, so one key closes the
  // top layer and only the top layer. Without a provider there is no stack and
  // no scrim, and the panel binds its own — see useHostPortal for why a
  // provider-less panel renders at all.
  const api = useLayer("drawer", live, onClose);
  useEscape(live && !api, onClose);
  useFocusTrap(panel, live);
  useReturnFocus(live, panel, initialFocus);

  return useHostPortal(
    <div
      ref={panel}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className={cx("k-drawer", className)}
      data-state={state}
    >
      <header className="k-drawer__head">
        <div className="k-drawer__head-row">
          <div className="k-drawer__titles">
            {eyebrow ? <span className="k-meta">{eyebrow}</span> : null}
            <h2 className="k-drawer__title">{title}</h2>
          </div>
          <IconButton icon={X} label={closeLabel} onClick={onClose} />
        </div>
        {meta ? <span className="k-meta">{meta}</span> : null}
      </header>
      <div className="k-drawer__body">{children}</div>
    </div>,
    live,
  );
}

/* ═══ 56, 57 · modals ═══════════════════════════════════════════════════════
   56 confirm  width 440 · pad 24 · radius 16 · button row gap 12
   57 form     width 560 · pad 24 · radius 16 · field gap 16

   One component, two widths. The specimens draw the same box: same padding,
   same radius, same overlay surface, same border, same shadow, and a footer
   row of actions. Splitting them into two components would mean two places to
   change the radius.
   ═════════════════════════════════════════════════════════════════════════ */

export interface ModalProps extends Stateful {
  open: boolean;
  onClose: () => void;
  title: string;
  /** "confirm" is item 56 at 440, "form" is item 57 at 560. */
  size?: "confirm" | "form";
  /** The sentence under the title. Item 56 only. */
  description?: ReactNode;
  children?: ReactNode;
  /** The action row. Rendered right-aligned with a gap of 12. */
  actions?: ReactNode;
  /** Item 56: "Focus opens on the cancel control for destructive actions."
      Item 57: "Focus opens on the first field." So the caller says which. */
  initialFocus?: RefObject<HTMLElement | null>;
  className?: string;
}

export function Modal({
  open, onClose, title, size = "confirm", description, children, actions,
  initialFocus, state, className,
}: ModalProps) {
  const panel = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const live = open || state === "open";

  const api = useLayer("modal", live, onClose);
  useEscape(live && !api, onClose);
  useFocusTrap(panel, live);
  useReturnFocus(live, panel, initialFocus);

  return useHostPortal(
    <div
      ref={panel}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className={cx("k-modal", size === "form" && "k-modal--form", className)}
      data-state={state}
    >
      <div className="k-modal__head">
        <h2 className="k-modal__title" id={titleId}>{title}</h2>
        {description ? <p className="k-modal__desc">{description}</p> : null}
      </div>
      {children ? <div className="k-modal__body">{children}</div> : null}
      {actions ? (
        <div className={cx("k-modal__actions", size === "form" && "k-modal__actions--ruled")}>
          {actions}
        </div>
      ) : null}
    </div>,
    live,
  );
}

export type { KitState };
